import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { doorNumberVariables, logoPlacementVariables, numberWobble, } from '../field-state/calendar-design.js';
import { GRACE_WINDOW_LABEL, fieldTermSingular } from '../field-state/labels.js';
/**
 * A single field ("Türchen"/"Feld") of the calendar.
 *
 * THE DOOR. Every field has a door in front of what it holds. Closed on the days to come,
 * half open today, gone on the days that have passed. The sponsor's logo shows in every one
 * of them (D30): the sponsors pay for the season, so a closed door is see-through and carries
 * the logo with its number. Only an emergency stop takes it away. The door is drawn by
 * `field.css` from the state alone; this component only lays out the layers.
 *
 * Renders completely without client JavaScript: state, shape and the individual
 * grace window overlay are expressed through class names and data attributes
 * that `field.css` styles. Hydrating this component is possible but never
 * required; the server rendered markup is the finished product
 * (concept 11.9 "statisch zuerst").
 *
 * Accessibility: the state is in the text, not only in the colour or the door. The
 * markup order is the reading order (number, state, notice, logo), so a screen reader
 * announces "Türchen 7, Heute geöffnet, Teilnahme an der Verlosung heute bis 23:59 Uhr,
 * <logo>". The advertising notice stands once on the page (`SponsoredNotice`, D28). Most
 * of those words are visually hidden on the tile; the door is decoration and hidden from
 * assistive technology, including its copy of the number.
 *
 * A non-interactive tile is a plain `div` and carries **no** `aria-disabled`. The
 * attribute only means something on an element whose role knows a disabled state;
 * on a generic container it is ignored by assistive technology while looking like
 * a promise in the markup. What actually conveys the state is the text: a closed
 * field says "noch verschlossen", and that is announced whether or not anything
 * supports the attribute.
 */
export function FieldTile({ position, presentation, size = 'single', logo = null, sponsorName = null, href, target, numberStyle = null, logoPlacement = null, numberWobble: wobble = 0, decor = null, fieldTerm = 'field', shape = 'square', className, }) {
    const isInteractive = presentation.interaction !== 'none' && href !== undefined;
    const stopped = presentation.state === 'depublished';
    const shownLogo = stopped ? null : logo;
    const shownName = stopped || shownLogo !== null ? null : sponsorName;
    const hasMark = shownLogo !== null || shownName !== null;
    const inGraceWindow = presentation.graceWindowEndsAt !== null;
    const classNames = [
        'countdown-field',
        `countdown-field--shape-${shape}`,
        size === 'single' ? null : `countdown-field--${size}`,
        hasMark ? 'countdown-field--has-mark' : null,
        numberStyle === null ? null : 'countdown-field--numeral-styled',
        logoPlacement === null ? null : 'countdown-field--logo-placed',
        className,
    ]
        .filter(Boolean)
        .join(' ');
    // What is behind the door: the logo, or the name while there is none.
    let mark = null;
    if (shownLogo !== null) {
        mark = (_jsx("img", { className: "countdown-field__logo", src: shownLogo.src, alt: shownLogo.alt, loading: "lazy", decoding: "async" }));
    }
    else if (shownName !== null) {
        mark = _jsx("span", { className: "countdown-field__sponsor-name", children: shownName });
    }
    const body = (_jsxs(_Fragment, { children: [_jsxs("span", { className: "countdown-field__numeral", children: [_jsx("span", { className: "countdown-sr-only", children: `${fieldTermSingular(fieldTerm)} ` }), position] }), _jsx("span", { className: "countdown-field__status", children: inGraceWindow ? GRACE_WINDOW_LABEL : presentation.label }), presentation.notice !== null ? (_jsx("span", { className: "countdown-sr-only", children: presentation.notice.text })) : null, _jsx("span", { className: "countdown-field__marker", "aria-hidden": "true", children: presentation.marker }), _jsx("span", { className: "countdown-field__reveal", children: mark }), _jsx("span", { className: "countdown-field__door", "aria-hidden": "true", children: _jsx("span", { className: "countdown-field__door-number", children: position }) }), _jsx("span", { className: "countdown-field__leaves", "aria-hidden": "true" })] }));
    const tilt = numberWobble(position, wobble);
    const variables = {
        ...(numberStyle === null ? {} : doorNumberVariables(numberStyle)),
        ...(logoPlacement === null ? {} : logoPlacementVariables(logoPlacement)),
        ...(tilt === 0 ? {} : { '--countdown-numeral-wobble': `${String(tilt)}deg` }),
    };
    return (_jsx("li", { className: classNames, style: Object.keys(variables).length === 0 ? undefined : variables, "data-field-state": presentation.state, "data-field-position": position, ...(decor === null ? {} : { 'data-decor': decor.ornament, 'data-decor-corner': decor.corner }), ...(inGraceWindow ? { 'data-grace-window': 'open' } : {}), children: isInteractive ? (_jsx("a", { className: "countdown-field__body", href: href, target: target, rel: target === undefined ? undefined : 'noopener', children: body })) : (_jsx("div", { className: "countdown-field__body", children: body })) }));
}
//# sourceMappingURL=FieldTile.js.map